import { useState, useEffect, useRef } from "react";
import {
  getUser, getLocalEntries, addLocalEntry,
  hasTodayLocal, todayKey, getQuestion, getAltPrompt,
} from "@/store";
import ProtectBanner from "@/components/ProtectBanner";

const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/* Auto-expand textarea — no fixed height fights iOS keyboard */
function useAutoResize(ref, value) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.max(120, el.scrollHeight) + "px";
  }, [value, ref]);
}

export default function Checkin({ showToast }) {
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);
  const [doneText, setDoneText] = useState("");
  const [showProtect, setShowProtect] = useState(false);
  const [protectDismissed, setProtectDismissed] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  const [uniqueDays, setUniqueDays] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const textareaRef = useRef(null);
  useAutoResize(textareaRef, text);

  const user = getUser();
  const question = getQuestion();
  const altPrompt = getAltPrompt();
  const today = new Date();
  const dateLabel = `${DAYS[today.getDay()]} \u00b7 ${MONTHS[today.getMonth()]} ${today.getDate()}`;
  const hour = today.getHours();
  const greeting =
    (hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening") +
    (user.name ? `, ${user.name}` : "") + ".";

  useEffect(() => {
    if (hasTodayLocal()) {
      const e = getLocalEntries().find((e) => e.date === todayKey());
      if (e) { setDone(true); setDoneText(e.text); }
    }
    updateStats();
  }, []);

  function updateStats() {
    const entries = getLocalEntries();
    setTotalEntries(entries.length);
    setUniqueDays(new Set(entries.map((e) => e.date)).size);
  }

  // Show protect banner at 5 entries
  useEffect(() => {
    if (protectDismissed) return;
    if (getLocalEntries().length >= 5) setShowProtect(true);
  }, [protectDismissed, done]);

  async function submit() {
    const trimmed = text.trim();
    if (trimmed.length < 5 || submitting) return;
    setSubmitting(true);
    textareaRef.current?.blur();

    try {
      const updated = addLocalEntry(trimmed, todayKey());
      if (updated.length >= 5) setShowProtect(true);
      updateStats();
      setDone(true);
      setDoneText(trimmed);
      showToast("Logged. Your hypha grows.");
    } catch {
      showToast("Failed to save.");
    } finally {
      setSubmitting(false);
    }
  }

  const ready = text.trim().length > 10;

  return (
    <div className="scroll-view scroll-view-padded" style={{ padding: "24px 20px 0" }} data-testid="checkin-view">
      {/* Protect banner */}
      {showProtect && !protectDismissed && (
        <ProtectBanner
          onAuthSuccess={() => {}}
          onDismiss={() => { setProtectDismissed(true); setShowProtect(false); }}
        />
      )}

      {/* Date */}
      <p style={{
        fontFamily: "var(--mono)", fontSize: 11, fontWeight: 300,
        letterSpacing: "0.16em", color: "var(--teal)",
        textTransform: "uppercase", marginBottom: 4,
      }}>
        {dateLabel}
      </p>

      {/* Greeting */}
      <p style={{
        fontSize: "clamp(18px, 5vw, 22px)",
        fontWeight: 400, color: "var(--cream)",
        marginBottom: 24, lineHeight: 1.3,
      }}>
        {greeting}
      </p>

      {/* Done state */}
      {done ? (
        <div
          data-testid="checkin-done-state"
          style={{
            background: "var(--teal-glow)",
            border: "1px solid var(--teal-border)",
            borderRadius: 12, padding: "24px 20px",
            marginBottom: 20, animation: "fadeRise 0.4s ease",
          }}
        >
          <div style={{
            width: 44, height: 44, borderRadius: "50%",
            background: "rgba(29,158,117,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 14px",
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
              stroke="#1D9E75" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 10 8 14 16 6" />
            </svg>
          </div>
          <p style={{ fontSize: 20, fontStyle: "italic", color: "var(--cream)", textAlign: "center", marginBottom: 4 }}>
            Logged for today.
          </p>
          <p style={{
            fontFamily: "var(--mono)", fontSize: 11, fontWeight: 300,
            color: "var(--cream-faint)", letterSpacing: "0.06em",
            textAlign: "center", marginBottom: 18,
          }}>
            Come back tomorrow.
          </p>
          <div style={{ borderLeft: "2px solid var(--teal)", paddingLeft: 14 }}>
            <p style={{ fontSize: 15, fontStyle: "italic", color: "var(--cream-dim)", lineHeight: 1.7 }}>
              "{doneText}"
            </p>
          </div>
        </div>
      ) : (
        /* Check-in form */
        <div
          data-testid="checkin-form"
          style={{
            background: "var(--teal-glow)",
            border: "1px solid var(--teal-border)",
            borderRadius: 12, marginBottom: 20, overflow: "hidden",
          }}
        >
          <div style={{ padding: "20px 20px 0" }}>
            <p style={{
              fontSize: "clamp(17px, 4.5vw, 20px)",
              fontStyle: "italic", color: "var(--cream)", lineHeight: 1.5,
            }}>
              "{question}"
            </p>
          </div>

          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write freely. No one grades this."
            rows={1}
            data-testid="checkin-textarea"
            style={{
              display: "block", width: "100%",
              fontSize: 16,
              fontFamily: "var(--serif)", fontStyle: "italic",
              color: "var(--cream)",
              background: "transparent",
              border: "none",
              borderTop: "1px solid rgba(29,158,117,0.18)",
              outline: "none", resize: "none",
              lineHeight: 1.7, padding: "16px 20px",
              overflow: "hidden", minHeight: 120,
            }}
          />

          <div style={{
            borderTop: "1px solid rgba(29,158,117,0.12)",
            padding: "12px 16px",
            display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 12,
          }}>
            <span style={{
              fontFamily: "var(--mono)", fontSize: 11, fontWeight: 300,
              color: "var(--cream-faint)", letterSpacing: "0.08em", flexShrink: 0,
            }}>
              Day {getLocalEntries().length + 1}
            </span>

            <button
              onClick={submit}
              disabled={!ready || submitting}
              data-testid="checkin-submit-btn"
              style={{
                flex: 1, maxWidth: 140, height: 44,
                background: ready ? "var(--teal)" : "transparent",
                color: ready ? "var(--deep)" : "var(--cream-faint)",
                border: ready ? "none" : "1px solid rgba(29,158,117,0.2)",
                borderRadius: 8,
                fontFamily: "var(--mono)", fontSize: 13, fontWeight: 400,
                letterSpacing: "0.06em",
                cursor: ready ? "pointer" : "default",
                transition: "background 0.2s, color 0.2s, transform 0.1s",
              }}
            >
              {submitting ? "..." : "Log it"}
            </button>
          </div>
        </div>
      )}

      {/* Alt prompt */}
      <div style={{
        borderLeft: "2px solid rgba(29,158,117,0.25)",
        paddingLeft: 14, marginBottom: 24,
      }}>
        <p style={{
          fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
          letterSpacing: "0.12em", color: "var(--teal)",
          textTransform: "uppercase", marginBottom: 5,
        }}>
          {altPrompt.label}
        </p>
        <p style={{ fontSize: 15, fontStyle: "italic", color: "var(--cream-dim)", lineHeight: 1.65 }}>
          "{altPrompt.text}"
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {[
          { value: totalEntries, label: "entries" },
          { value: uniqueDays, label: "days logged" },
        ].map((s) => (
          <div key={s.label} style={{
            background: "rgba(29,158,117,0.06)",
            border: "1px solid rgba(29,158,117,0.14)",
            borderRadius: 10, padding: "16px 12px", textAlign: "center",
          }}>
            <p style={{ fontSize: 30, fontWeight: 400, color: "var(--cream)", lineHeight: 1, marginBottom: 4 }} data-testid={`stat-${s.label.replace(" ", "-")}`}>
              {s.value}
            </p>
            <p style={{
              fontFamily: "var(--mono)", fontSize: 10, fontWeight: 300,
              letterSpacing: "0.12em", color: "var(--cream-faint)",
              textTransform: "uppercase",
            }}>
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
